/**
 * RREGULLIMI I ORARIT — departamenti, projektet, periudha dhe pauzat, në një ekran.
 *
 * Deri tani e njëjta punë bëhej te një shirit pluskues në fund të Planit të
 * Punës: dy panele të palosura, fusha 24-pikësh dhe një përmbledhje e ngjeshur
 * në cep. Aty mbaronte edhe kuptimi i tij — një shirit është për një veprim të
 * shpejtë, kurse kjo është një formë me katër vendime dhe një bilanc që duhet
 * të mbyllet.
 *
 * ÇFARË E MBAN TË NDERSHËM KËTË EKRAN:
 *
 *   · **Bilanci lart, gjithmonë i dukshëm.** «Orari total» kundrejt «Alokuar»
 *     është pyetja e vetme që vendos nëse forma është e plotë. Poshtë tabelës
 *     do të lexohej vetëm nga kush shkon deri aty.
 *   · **Orët nuk shkruhen — llogariten.** Kolona «ORËT» dhe «KOHËZGJATJA» janë
 *     rrjedhojë e dy orëve, ndaj nuk janë fusha. Një fushë e tretë që duhej të
 *     pajtohej me dy të tjerat do të ishte një e vërtetë e tretë.
 *   · **Asnjë rregull i ri.** Trupi që dërgohet është pikërisht ai i mëparshmi
 *     (`/api/workSchedules/:id/assign`); ndryshoi vendi ku plotësohet, jo çfarë.
 *
 * PAUZAT janë NGA–DERI këtu, kurse API-ja pret `{time, min}`. Kthimi bëhet në
 * `toPayload()`: ora e fillimit shkon si `time`, gjatësia si `min`. Kështu
 * njeriu shkruan atë që di (kur nis e kur mbaron) dhe serveri merr atë që pret.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton,
  TextField, MenuItem, Divider, Checkbox, FormControlLabel, Tooltip, alpha,
} from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ApartmentRoundedIcon      from '@mui/icons-material/ApartmentRounded';
import FolderOpenRoundedIcon     from '@mui/icons-material/FolderOpenRounded';
import ScheduleRoundedIcon       from '@mui/icons-material/ScheduleRounded';
import AddRoundedIcon            from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon  from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon    from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon   from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon          from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon          from '@mui/icons-material/CloseRounded';
import EastRoundedIcon           from '@mui/icons-material/EastRounded';
import WorkingTypeSelect, { PaidChip } from '../common/WorkingTypeSelect';
import TimeField from '../common/TimeField';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { authFetch } from '../../services/apiServices';

/* ─── Koha si minuta — një kthim, i përdorur nga të gjitha llogaritjet ────── */
export const toMin = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  if (!m) return null;
  const h = +m[1], mi = +m[2];
  return h > 24 || mi > 59 ? null : h * 60 + mi;
};

/**
 * Gjatësia e një intervali, me mesnatën e kaluar si turn nate.
 * 22:00→06:00 është tetë orë, jo minus gjashtëmbëdhjetë.
 */
export const spanMin = (from, to) => {
  const a = toMin(from), b = toMin(to);
  if (a === null || b === null) return 0;
  return b >= a ? b - a : (b + 1440) - a;
};

export const fmtHM = (m) => {
  const a = Math.max(0, Math.round(m || 0));
  const h = Math.floor(a / 60), mm = a % 60;
  return h ? (mm ? `${h}h ${mm}m` : `${h}h`) : `${mm}m`;
};

/**
 * Gjendja e formës → trupi që pret `/api/workSchedules/:id/assign`.
 *
 * I ekspozuar dhe i pastër me qëllim: kthimi nga NGA–DERI te `{time, min}` është
 * i vetmi vend ku dy modelet takohen, dhe testohet pa montuar asnjë ekran.
 */
/**
 * Tipi i ditës si rrjedhojë e rreshtave: një kod nëse të gjithë pajtohen, `null`
 * nëse dita është e përzier ose s'është vendosur asgjë.
 *
 * `null` do të thotë «trashëgo shabllonin» — pikërisht sjellja e mëparshme.
 */
export function dayTypeOf(form) {
  const codes = new Set((form?.projects || []).map((p) => p.workingTypeCode).filter(Boolean));
  return codes.size === 1 ? [...codes][0] : null;
}

export function toPayload(form) {
  return {
    department_id: form.departmentId || null,
    project_segments: (form.projects || [])
      .filter((p) => p.projectId)
      // `working_type_code` bosh → segmenti trashëgon tipin e caktimit. Kështu
      // një orar me një tip të vetëm mbetet i tillë pa e shkruar njeriu tri herë.
      .map((p) => ({
        project_id: Number(p.projectId),
        start_time: p.from || null,
        end_time:   p.to   || null,
        working_type_code: p.workingTypeCode || null,
      })),
    effective_from: form.from || null,
    effective_to:   form.noEnd ? null : (form.to || null),
    // Tipi i DITËS nuk shkruhet më veçmas — rrjedh nga rreshtat. Një kontroll i
    // dytë mbi të njëjtin koncept do të kërkonte një rregull se cili fiton, dhe
    // asnjë rregull i tillë s'do të ishte i dukshëm në ekran.
    //
    // Një ditë e përzier NUK zgjidhet me «tipi që zë më shumë minuta»: kolona e
    // ditës mban një kod, dhe një kod i vetëm s'e përshkruan dot atë ditë. Në
    // atë rast mbetet `null` — pra shablloni — kurse ndarja e vërtetë rri te
    // segmentet, ku është e plotë. Më mirë asnjë përgjigje se një e trilluar.
    working_type_code: dayTypeOf(form),
    // Pauza E GJATË dhe ato TË SHKURTRA janë dy gjëra të ndara te API-ja, dhe
    // janë dy gjëra të ndara edhe në punë: e gjata është pauza zyrtare e orarit
    // (dreka), të shkurtrat janë ato që merren gjatë ditës. Të mbledhura në një
    // shifër, askush s'do ta dinte nëse dikush hëngri drekë apo bëri gjashtë
    // pauza nga pesë minuta.
    //
    // E gjata është NJË — kolona e saj është një numër i vetëm — ndaj merret
    // rreshti i shënuar si i tillë, dhe pjesa tjetër shkon te të shkurtrat.
    long_break_minutes: (() => {
      const lb = (form.breaks || []).find((b) => b.kind === 'long' && b.from && b.to);
      return lb ? spanMin(lb.from, lb.to) : null;
    })(),
    // Lloji i pauzës së orarit ka nevojë për kolonë të vetën, sepse ajo është
    // një numër i vetëm; ato shtesë e mbajnë brenda JSONB-së së tyre.
    long_break_type_code: (() => {
      const lb = (form.breaks || []).find((b) => b.kind === 'long' && b.from && b.to);
      return lb?.workingTypeCode || null;
    })(),
    // Ora, jo vetëm gjatësia: forma e di kur bie pauza, dhe deri tani ajo humbte
    // me ruajtjen — pra plani kishte një pauzë pa vend mbi rrjetë.
    long_break_start: (() => {
      const lb = (form.breaks || []).find((b) => b.kind === 'long' && b.from && b.to);
      return lb?.from || null;
    })(),
    short_breaks: (form.breaks || [])
      .filter((b) => b.from && b.to && b.kind !== 'long')
      .map((b) => ({ time: b.from, min: spanMin(b.from, b.to),
                     working_type_code: b.workingTypeCode || null })),
    description: (form.description || '').trim() || null,
  };
}

/* ─── Blloqe të vogla, që stili të mos ripërsëritet rresht pas rreshti ────── */
const Section = ({ icon, title, hint, children, sx }) => (
  <Box sx={{ ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: .6, mb: .5 }}>
      {icon}
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '.07em',
        textTransform: 'uppercase', color: 'text.secondary' }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>{hint}</Typography>
      )}
    </Box>
    {children}
  </Box>
);

const HeadCell = ({ children, sx }) => (
  <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '.05em',
    textTransform: 'uppercase', color: 'text.secondary', ...sx }}>
    {children}
  </Typography>
);

/**
 * Përmasat në një vend.
 *
 * Forma ishte e gjatë sa nuk nxinte në ekran: fundi i saj mbulohej nga shiriti i
 * veprimeve dhe «Pauza totale» duhej kërkuar me rrotullim. Shkaku nuk ishte
 * përmbajtja — ajo është e vogël — por hapësira rreth saj, e shkruar veç në
 * çdo `sx`. Këtu janë tri numra; ndryshimi i tyre e ngjesh ose e liron tërë
 * formën në mënyrë të njëtrajtshme.
 */
const FIELD_H = 32;   // lartësia e çdo fushe
const CARD_P  = 1.5;  // brendësia e një kartele
const ROW_PY  = .5;   // lartësia e një rreshti tabele

/**
 * Një `<input type="time">` i duhet vend për «12:45» PLUS ikonën e orës që e
 * vizaton vetë shfletuesi. Nën këtë, teksti pritet dhe fusha shkruan «1» — dhe
 * askush s'e merr me mend se ç'orë mban. Prandaj ora nuk është kurrë kolonë
 * `fr`: `fr` do të thotë «merr ç'të mbetet», dhe ajo që mbetet varet nga
 * gjerësia e dritares.
 */
const TIME_W = 104;
// Rreshti i segmentit: numri, projekti, LLOJI, dy orët, shuma, fshirja.
const GRID_COLS = `32px 1fr 180px ${TIME_W}px ${TIME_W}px 76px 44px`;
// Rreshti i pauzës: dy orët, lloji, kohëzgjatja, fshirja. Lloji e merr mbetjen,
// sepse dy çipa tekst tkurren pa u prerë; ora jo.
// Rreshti i pauzës: dy orët, LLOJI nga fjalori, sa zgjat, çipi «e orarit», koshi.
const BRK_COLS  = `${TIME_W}px ${TIME_W}px 1fr 58px 58px 32px`;

const timeField = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, height: FIELD_H, fontSize: '0.82rem' },
  '& input': { py: 0 },
};

export default function ScheduleAllocationDialog({
  open, onClose, onSave, saving = false,
  workerCount = 0, workerIds = [], shiftStart = '', shiftEnd = '',
  departments = [], projects = [], initial = null,
  // Kur punëtorët e zgjedhur kanë profile që s'pajtohen, fusha rri bosh me
  // qëllim — dhe arsyeja thuhet. Pa të, një fushë e pambushur do të dukej si
  // gabim i sistemit e jo si vendim që pret njeriun.
  mixedDepartment = false, mixedProject = false,
}) {
  const empty = useMemo(() => ({
    departmentId: '', projects: [], from: '', to: '', noEnd: true,
    breaks: [], description: '',
  }), []);
  const [form, setForm] = useState(empty);
  // Kod → përkufizim, mbledhur nga vetë rreshtat kur njeriu zgjedh. Fundi i
  // formës duhet të thotë «paguhet / s'paguhet», dhe atë e di vetëm zgjedhësi.
  const [typeMeta, setTypeMeta] = useState({});

  /**
   * Çfarë ka TASHMË ky interval.
   *
   * Ruajtja e zëvendëson caktimin ekzistues në heshtje — `preserveOutsideSelection`
   * e ndan të vjetrin, ruan pjesët jashtë intervalit dhe e shënon pjesën brenda
   * si të kaluar. Sjellja është e saktë; ajo që mungonte ishte fjala. Dikush që
   * cakton një orar mbi një tjetër duhet ta dijë PARA se ta bëjë, jo ta zbulojë
   * kur dikush ankohet se i ndërroi turni.
   *
   * Kontrolli varet nga datat që shkruhen, ndaj rri këtu — jo te faqja, që i mëson
   * ato vetëm pas ruajtjes.
   */
  const [existing, setExisting] = useState([]);
  // Pushimet e aprovuara në të njëjtin interval: serveri e refuzon caktimin
  // mbi to me 409, ndaj forma duhet ta thotë para se ta provojë.
  const [leaves, setLeaves] = useState([]);
  useEffect(() => {
    if (!open || !form.from || !workerIds.length) { setExisting([]); setLeaves([]); return; }
    let alive = true;
    // Pritje e shkurtër: datat shkruhen shifër për shifër, dhe një kërkesë për
    // çdo shifër do t'i dërgonte gjashtë për një datë të vetme.
    const id = setTimeout(async () => {
      try {
        const q = new URLSearchParams({
          worker_ids: workerIds.join(','),
          from: form.from,
          ...(form.noEnd ? {} : (form.to ? { to: form.to } : {})),
        });
        const r = await authFetch(`/api/workSchedules/assignments?${q}`);
        const j = await r.json();
        if (alive) { setExisting(j?.success ? (j.data || []) : []);
                     setLeaves(j?.success ? (j.leaves || []) : []); }
      } catch { if (alive) { setExisting([]); setLeaves([]); } }
    }, 400);
    return () => { alive = false; clearTimeout(id); };
  // Çelësi është vargu, jo referenca: edhe një prind i pakujdesshëm që e rikrijon
  // listën në çdo render nuk e rinis dot kërkesën pa ndryshuar përmbajtja.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.from, form.to, form.noEnd, workerIds.join(',')]);

  // Snapshot the initial values when opening; parent refreshes must preserve edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setForm({ ...empty, ...(initial || {}) }); }, [open]);

  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const patchRow = (key, i, patch) =>
    setForm((p) => ({ ...p, [key]: p[key].map((r, ri) => (ri === i ? { ...r, ...patch } : r)) }));
  const dropRow = (key, i) =>
    setForm((p) => ({ ...p, [key]: p[key].filter((_, ri) => ri !== i) }));

  /* ── Bilanci: sa nga turni është alokuar ─────────────────────────────────
   * Kjo është pyetja që vendos nëse forma është e plotë, ndaj rri te koka. */
  // Tipi i ditës është rrjedhojë e rreshtave, jo fushë më vete — ndaj llogaritet
  // këtu dhe shkruhet vetëm një herë, te fundi.
  const rowCodes = useMemo(
    () => [...new Set((form.projects || []).map((p) => p.workingTypeCode).filter(Boolean))],
    [form.projects],
  );
  const dayType  = rowCodes.length === 1 ? typeMeta[rowCodes[0]] || null : null;
  const dayLabel = rowCodes.length === 0 ? 'Si shablloni (PRODUCTIVE)'
    : rowCodes.length === 1 ? (dayType?.name || rowCodes[0])
    // Një ditë e përzier nuk redukton dot në një emër; numri i llojeve e thotë
    // më shumë se cilido prej tyre i zgjedhur si «kryesori».
    : `${rowCodes.length} lloje sipas segmenteve`;

  const shiftMin     = spanMin(shiftStart, shiftEnd);
  const allocatedMin = form.projects.reduce((s, p) => s + spanMin(p.from, p.to), 0);
  const longMin      = form.breaks.filter((b) => b.kind === 'long')
                          .reduce((s, b) => s + spanMin(b.from, b.to), 0);
  const shortMin     = form.breaks.filter((b) => b.kind !== 'long')
                          .reduce((s, b) => s + spanMin(b.from, b.to), 0);
  const breakMin     = longMin + shortMin;
  const remaining    = shiftMin - allocatedMin;
  const balanced     = shiftMin > 0 && remaining === 0;

  const addProject = () => setForm((p) => {
    // Segmenti i ri niset aty ku mbaroi i mëparshmi — ndarja e orëve nuk guxon
    // ta numërojë të njëjtën orë dy herë.
    const last = p.projects[p.projects.length - 1];
    return { ...p, projects: [...p.projects, {
      id: `p${p.projects.length}-${p.projects.length ? last.to : shiftStart}`,
      projectId: '', from: last?.to || shiftStart, to: shiftEnd,
      // Bosh me qëllim: trashëgon tipin e orarit. Po ta kopjonim atë të
      // rreshtit të mëparshëm, një ndryshim i tipit të orarit s'do ta prekte
      // më këtë rresht — dhe askush s'do ta dinte pse.
      workingTypeCode: '',
    }] };
  });

  const addBreak = () => setForm((p) => {
    const last = p.breaks[p.breaks.length - 1];
    const startMin = (toMin(last?.to) ?? (toMin(shiftStart) ?? 8 * 60) + 90) + (last ? 90 : 0);
    const hh = String(Math.floor(startMin / 60) % 24).padStart(2, '0');
    const mm = String(startMin % 60).padStart(2, '0');
    const endMin = startMin + 15;
    const eh = String(Math.floor(endMin / 60) % 24).padStart(2, '0');
    const em = String(endMin % 60).padStart(2, '0');
    // E para që shtohet është pauza e gjatë (dreka) — ajo është e zakonshmja;
    // të tjerat vijnë si të shkurtra derisa dikush të thotë ndryshe.
    const kind = p.breaks.some((b) => b.kind === 'long') ? 'short' : 'long';
    return { ...p, breaks: [...p.breaks, { id: `b${p.breaks.length}-${hh}${mm}`, kind,
      // Bosh: llojin e zgjedh njeriu nga fjalori i vet, s'e shpik forma.
      workingTypeCode: '', from: `${hh}:${mm}`, to: `${eh}:${em}` }] };
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.default', maxHeight: '92vh' } }}>

      {/* ── Koka: kush jemi, çfarë bëjmë, dhe a mbyllet bilanci ────────── */}
      <Box sx={{ px: 1.75, pt: 1.15, pb: 1.15, display: 'flex', alignItems: 'center', gap: 1.25,
        flexWrap: 'wrap', bgcolor: 'background.paper' }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0,
          bgcolor: 'primary.main', color: 'primary.contrastText',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EventAvailableRoundedIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.25 }}>
            Rregullimi i orarit
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            Aloko departamentin dhe projektet për këtë orar
            {workerCount > 0 && ` · ${workerCount} punëtor${workerCount === 1 ? '' : 'ë'}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0,
          border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
          bgcolor: 'background.default', overflow: 'hidden', flexShrink: 0 }}>
          {[
            { label: 'Orari total', value: fmtHM(shiftMin) },
            { label: 'Alokuar',     value: fmtHM(allocatedMin), accent: true },
          ].map((s) => (
            <Box key={s.label} sx={{ px: 1.5, py: .85, borderRight: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}>{s.label}</Typography>
              <Typography className="tabular" sx={{ fontSize: '0.98rem', fontWeight: 800, lineHeight: 1.25,
                color: s.accent ? 'primary.main' : 'text.primary' }}>
                {s.value}
              </Typography>
            </Box>
          ))}
          {/* Verdikti, jo vetëm numrat: pa të, dy shifra të barabarta duhen
              krahasuar me sy sa herë preket një orë. */}
          <Box sx={{ px: 1.5, py: .85, display: 'flex', alignItems: 'center', gap: .85 }}>
            {balanced
              ? <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 21 }} />
              : <ErrorOutlineRoundedIcon sx={{ color: remaining < 0 ? 'error.main' : 'warning.main', fontSize: 21 }} />}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.3,
                color: balanced ? 'success.main' : remaining < 0 ? 'error.main' : 'warning.main' }}>
                {balanced ? 'Përputhet' : remaining < 0 ? 'Tejkalim' : 'Mbetur'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}>
                {balanced ? 'Alokimi është i plotë' : `${fmtHM(Math.abs(remaining))} ${remaining < 0 ? 'mbi orar' : 'pa alokuar'}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />

      <DialogContent sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

        {/* ── Departamenti + projektet ────────────────────────────────── */}
        <Box sx={{ p: CARD_P, borderRadius: 2.5, bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider' }}>
          <Section icon={<ApartmentRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
            title="Departamenti">
            <TextField
              select fullWidth size="small" value={form.departmentId}
              onChange={(e) => f('departmentId', e.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: FIELD_H + 4, fontSize: '0.85rem' } }}
            >
              <MenuItem value=""><em style={{ color: '#9CA3AF' }}>Zgjidh departamentin</em></MenuItem>
              {departments.map((d) => {
                const id = String(d.id ?? d.Id);
                return <MenuItem key={id} value={id}>{d.name || d.Name}</MenuItem>;
              })}
            </TextField>
            {mixedDepartment && !form.departmentId && (
              <Typography sx={{ mt: .75, fontSize: '0.78rem', color: 'warning.main', fontWeight: 600 }}>
                Punëtorët e zgjedhur kanë departamente të ndryshme te profilet — zgjidhe me dorë.
              </Typography>
            )}
          </Section>
          <Section sx={{ mt: 1.5 }}
            icon={<FolderOpenRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
            title="Projektet e alokuara" hint="secili segment mund të ketë llojin e vet të punës">
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS,
                gap: 1.25, px: 1.25, py: .7, bgcolor: 'action.hover' }}>
                <HeadCell>#</HeadCell><HeadCell>Projekti</HeadCell>
                <HeadCell>Lloji</HeadCell>
                <HeadCell>Nga</HeadCell><HeadCell>Deri</HeadCell>
                <HeadCell>Orët</HeadCell><HeadCell />
              </Box>

              {form.projects.length === 0 && (
                <Typography sx={{ px: 1.25, py: 1.25, fontSize: '0.8rem', color: 'text.secondary' }}>
                  Asnjë projekt i alokuar — orari do të caktohet pa ndarje projekti,
                  me një lloj pune për tërë turnin.
                </Typography>
              )}

              {form.projects.map((p, i) => (
                <Box key={p.id} sx={{ display: 'grid',
                  gridTemplateColumns: GRID_COLS, gap: 1.25,
                  px: 1.25, py: ROW_PY, alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: 'action.hover',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>{i + 1}</Box>
                  <TextField select fullWidth size="small" value={p.projectId}
                    onChange={(e) => patchRow('projects', i, { projectId: e.target.value })}
                    SelectProps={{ displayEmpty: true }} sx={timeField}>
                    <MenuItem value=""><em style={{ color: '#9CA3AF' }}>Zgjidh projektin</em></MenuItem>
                    {projects.map((pr) => {
                      const id = String(pr.id ?? pr.Id);
                      return <MenuItem key={id} value={id}>{pr.name || pr.Name}</MenuItem>;
                    })}
                  </TextField>
                  {/* Tipi i këtij segmenti. Bosh = trashëgon atë të orarit, dhe
                      pikërisht ashtu e thotë fusha — jo «zgjidh diçka». */}
                  <WorkingTypeSelect label="" value={p.workingTypeCode || ''}
                    dense emptyLabel="Si shablloni"
                    onChange={(code, t) => {
                      patchRow('projects', i, { workingTypeCode: code || '' });
                      if (t) setTypeMeta((m) => ({ ...m, [t.code]: t }));
                    }}
                    sx={timeField} />
                  <TimeField value={p.from || ''}
                    onChange={(v) => patchRow('projects', i, { from: v })} sx={timeField} />
                  {/* «Deri» e di ku nisi: lista shkruan sa zgjat secila orë, që
                      kohëzgjatja të mos llogaritet me mend para se të zgjidhet. */}
                  <TimeField value={p.to || ''} relativeTo={p.from || null}
                    onChange={(v) => patchRow('projects', i, { to: v })} sx={timeField} />
                  {/* Rrjedhojë e dy orëve — ndaj tekst, jo fushë. */}
                  <Typography className="tabular" sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    {fmtHM(spanMin(p.from, p.to))}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Tooltip title="Hiq këtë alokim">
                      <IconButton size="small" onClick={() => dropRow('projects', i)}
                        sx={{ color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, .08),
                          '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, .16) } }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Paralajmërimi rri KËTU, mbi butonin e ruajtjes dhe nën datat që e
                shkaktojnë — jo si toast pas veprimit, kur s'ka më çfarë të vendoset. */}
            {/* Ndalim, jo paralajmërim: leja është vendim i marrë dhe i miratuar.
                Një orar mbi të krijon një ditë që thotë dy gjëra njëherësh —
                «duhet të punojë» dhe «është i lejuar të mos punojë». */}
            {leaves.length > 0 && (
              <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, display: 'flex', gap: 1,
                bgcolor: (t) => alpha(t.palette.error.main, .08),
                border: '1px solid', borderColor: 'error.main' }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'error.main', flexShrink: 0, mt: .1 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'error.main' }}>
                    Orari nuk caktohet mbi një pushim të aprovuar
                  </Typography>
                  {leaves.slice(0, 6).map((l, i) => (
                    <Typography key={`${l.worker_id}-${i}`} sx={{ fontSize: '0.76rem', color: 'text.secondary', lineHeight: 1.5 }}>
                      <b>{l.worker_name}</b> · {l.type_name} · {String(l.start_date).slice(0, 10)}–{String(l.end_date).slice(0, 10)}
                    </Typography>
                  ))}
                  <Typography sx={{ mt: .5, fontSize: '0.76rem', color: 'text.secondary' }}>
                    Ndrysho periudhën, ose hiq punëtorët me pushim nga zgjedhja.
                  </Typography>
                </Box>
              </Box>
            )}

            {existing.length > 0 && (
              <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, display: 'flex', gap: 1,
                bgcolor: (t) => alpha(t.palette.warning.main, .1),
                border: '1px solid', borderColor: 'warning.main' }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0, mt: .1 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'warning.dark' }}>
                    {existing.length === 1
                      ? 'Një caktim ekziston tashmë për këtë interval'
                      : `${existing.length} caktime ekzistojnë tashmë për këtë interval`}
                  </Typography>
                  {existing.slice(0, 6).map((e) => (
                    <Typography key={e.id} sx={{ fontSize: '0.76rem', color: 'text.secondary', lineHeight: 1.5 }}>
                      <b>{e.worker_name}</b> · {e.schedule_name} ({e.start_time}–{e.end_time})
                      {' · '}{String(e.effective_from).slice(0, 10)}
                      {e.effective_to ? `–${String(e.effective_to).slice(0, 10)}` : ' → pa datë përfundimi'}
                    </Typography>
                  ))}
                  {existing.length > 6 && (
                    <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                      …edhe {existing.length - 6} të tjerë
                    </Typography>
                  )}
                  {/* Saktësisht çfarë ndodh — jo «do të mbishkruhet», që do të
                      linte të kuptohej se humbet edhe pjesa jashtë intervalit. */}
                  <Typography sx={{ mt: .5, fontSize: '0.76rem', color: 'text.secondary' }}>
                    Ruajtja e zëvendëson orarin e tyre <b>vetëm për datat e mësipërme</b>;
                    ditët jashtë këtij intervali mbeten si janë.
                  </Typography>
                </Box>
              </Box>
            )}

            {mixedProject && form.projects.some((p) => !p.projectId) && (
              <Typography sx={{ mt: .75, fontSize: '0.78rem', color: 'warning.main', fontWeight: 600 }}>
                Punëtorët e zgjedhur kanë projekte të ndryshme te profilet — zgjidhe me dorë.
              </Typography>
            )}

            <Button fullWidth onClick={addProject} startIcon={<AddRoundedIcon />}
              sx={{ mt: .85, py: .55, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                border: '1px dashed', borderColor: 'divider', color: 'primary.main' }}>
              Shto projekt
            </Button>
          </Section>
        </Box>

        {/* ── Periudha · Pauzat · Përshkrimi ──────────────────────────── */}
        <Box sx={{ display: 'grid', gap: 1.25, alignItems: 'stretch',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '0.75fr 1.55fr 0.85fr' } }}>

          <Box sx={{ p: CARD_P, borderRadius: 3, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider' }}>
            <Section icon={<ScheduleRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
              title="Periudha e vlefshmërisë">
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: .35 }}>Nga</Typography>
                  <TextField type="date" fullWidth size="small" value={form.from || ''}
                    onChange={(e) => f('from', e.target.value)} sx={timeField} />
                </Box>
                <EastRoundedIcon sx={{ fontSize: 16, color: 'text.disabled', mb: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: .35 }}>Deri</Typography>
                  <TextField type="date" fullWidth size="small" value={form.noEnd ? '' : (form.to || '')}
                    onChange={(e) => f('to', e.target.value)} disabled={form.noEnd}
                    inputProps={{ min: form.from || undefined }}
                    placeholder="Pa afat" sx={timeField} />
                </Box>
              </Box>
              {/* Kutia dhe fusha thonë të njëjtën gjë; kutia e thotë me fjalë,
                  që "pa datë" të mos ngatërrohet me "harrova ta plotësoj". */}
              <FormControlLabel
                sx={{ mt: .85, ml: 0 }}
                control={<Checkbox size="small" checked={!!form.noEnd}
                  onChange={(e) => f('noEnd', e.target.checked)} />}
                label={<Typography sx={{ fontSize: '0.8rem' }}>Pa datë përfundimi</Typography>}
              />
            </Section>
          </Box>

          <Box sx={{ p: CARD_P, borderRadius: 3, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider' }}>
            <Section icon={<ScheduleRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
              title="Pauzat" hint="(shumë pauza të lejuara)">
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: BRK_COLS, gap: .75,
                  px: 1.25, py: .6, bgcolor: 'action.hover' }}>
                  <HeadCell>Nga</HeadCell><HeadCell>Deri</HeadCell>
                  <HeadCell>Lloji</HeadCell>
                  {/* «Kohëzgjatja» dhe «Veprimi» shkeleshin mbi njëra-tjetrën te
                      70px dhe 34px. Kolona e koshit s'ka nevojë për emër. */}
                  <HeadCell>Zgjat</HeadCell><HeadCell>Orari</HeadCell><HeadCell />
                </Box>
                {form.breaks.length === 0 && (
                  <Typography sx={{ px: 1.25, py: 1.25, fontSize: '0.8rem', color: 'text.secondary' }}>
                    Pa pauza shtesë — vlen pauza e vetë orarit.
                  </Typography>
                )}
                {form.breaks.map((b, i) => (
                  <Box key={b.id} sx={{ display: 'grid', gridTemplateColumns: BRK_COLS, gap: .75,
                    px: 1.25, py: ROW_PY, alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                    {/* Hapi pesë-minutësh: një pauzë e çerekut nuk mjafton —
                        dhjetë minuta janë pauzë e plotë, çereku është drekë. */}
                    <TimeField value={b.from || ''} step={5}
                      onChange={(v) => patchRow('breaks', i, { from: v })} sx={timeField} />
                    <TimeField value={b.to || ''} step={5} relativeTo={b.from || null}
                      onChange={(v) => patchRow('breaks', i, { to: v })} sx={timeField} />
                    {/* Lloji vjen nga fjalori qendror, kategoria BREAK — pra nga
                        Cilësimet, ku e mban përdoruesi. «Gjatë/Shkurtër» ishte
                        një fjalor i dytë, i ngrirë në kod, për të njëjtën gjë. */}
                    <WorkingTypeSelect label="" value={b.workingTypeCode || ''}
                      dense categories={['BREAK']} emptyLabel="Pa lloj"
                      onChange={(code, t) => {
                        patchRow('breaks', i, { workingTypeCode: code || '' });
                        if (t) setTypeMeta((m) => ({ ...m, [t.code]: t }));
                      }}
                      sx={timeField} />
                    <Typography className="tabular" sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'primary.main' }}>
                      {spanMin(b.from, b.to)} min
                    </Typography>
                    {/* Kjo NUK është lloj, është strukturë: `long_break_minutes`
                        zëvendëson pauzën e shabllonit, dhe kolona mban një numër
                        të vetëm — ndaj vetëm një rresht mund ta mbajë. E fikur
                        te të gjitha = pauza e shabllonit mbetet e paprekur. */}
                    <Tooltip title={(b.kind || 'short') === 'long'
                      ? 'Kjo zëvendëson pauzën e shabllonit'
                      : 'Pauzë shtesë — pauza e shabllonit mbetet'}>
                      <Box
                        onClick={() => setForm((p) => ({ ...p, breaks: p.breaks.map((r, ri) => (
                          ri === i ? { ...r, kind: (r.kind || 'short') === 'long' ? 'short' : 'long' }
                            : { ...r, kind: 'short' })) }))}
                        sx={{ px: .6, py: .3, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center',
                          fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.4, border: '1px solid',
                          borderColor: (b.kind || 'short') === 'long' ? 'primary.main' : 'divider',
                          bgcolor: (t) => ((b.kind || 'short') === 'long' ? alpha(t.palette.primary.main, .1) : 'transparent'),
                          color: (b.kind || 'short') === 'long' ? 'primary.main' : 'text.disabled' }}>
                        E orarit
                      </Box>
                    </Tooltip>
                    <Box sx={{ textAlign: 'right' }}>
                      <Tooltip title="Hiq këtë pauzë">
                        <IconButton size="small" onClick={() => dropRow('breaks', i)}
                          sx={{ color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, .08),
                            '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, .16) } }}>
                          <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Button fullWidth onClick={addBreak} startIcon={<AddRoundedIcon />}
                sx={{ mt: .85, py: .55, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                  border: '1px dashed', borderColor: 'divider', color: 'primary.main' }}>
                Shto pauzë
              </Button>
              <Box sx={{ mt: .85, px: 1.25, py: .65, borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, .06),
                display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleRoundedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  E orarit <Box component="span" className="tabular" sx={{ fontWeight: 800, color: 'text.primary' }}>{longMin} min</Box>
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  · Shtesë <Box component="span" className="tabular" sx={{ fontWeight: 800, color: 'text.primary' }}>{shortMin} min</Box>
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>Gjithsej:</Typography>
                <Typography className="tabular" sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'primary.main' }}>
                  {breakMin} min
                </Typography>
              </Box>
            </Section>
          </Box>

          <Box sx={{ p: CARD_P, borderRadius: 3, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <Section icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
              title="Përshkrim" hint="(opsionale)"
              sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <TextField
                fullWidth multiline minRows={4} value={form.description}
                onChange={(e) => f('description', e.target.value.slice(0, 250))}
                placeholder="Shto përshkrim për këtë orar…"
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem', alignItems: 'flex-start', height: '100%' },
                  '& textarea': { height: '100% !important' } }}
              />
              <Typography sx={{ mt: .5, fontSize: '0.72rem', color: 'text.disabled', textAlign: 'right' }}>
                {form.description.length} / 250
              </Typography>
            </Section>
          </Box>
        </Box>
      </DialogContent>

      {/* ── Fundi: çfarë del vërtet nga kjo formë, dhe të dy veprimet ─── */}
      <Divider />
      <DialogActions sx={{ px: 1.75, py: .9, gap: 1, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, flexWrap: 'wrap' }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          {rowCodes.length === 1 && <PaidChip paid={dayType ? dayType.isPaid : null} accrue={dayType?.payrollImpact === 'ACCRUE'} />}
          {[
            ['Orari i punës', fmtHM(shiftMin), false],
            ['Pauza totale', `${breakMin} min`, false],
            // Ora efektive është ajo që paguhet — ndaj thuhet, jo lihet për ta zbritur.
            ['Orari efektiv', fmtHM(Math.max(0, shiftMin - breakMin)), true],
            ['Lloji', dayLabel, false],
          ].map(([label, value, accent], i) => (
            <React.Fragment key={label}>
              {i > 0 && <Typography sx={{ color: 'text.disabled' }}>•</Typography>}
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {label}: <Box component="span" className="tabular"
                  sx={{ fontWeight: 800, color: accent ? 'primary.main' : 'text.primary' }}>{value}</Box>
              </Typography>
            </React.Fragment>
          ))}
        </Box>
        <Button onClick={onClose} startIcon={<CloseRoundedIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2, py: .7, fontSize: '0.82rem',
            border: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
          Anulo
        </Button>
        {/* Butoni bie bashkë me rregullin: serveri e refuzon me 409, dhe një
            buton që dërgon një kërkesë të refuzuar me siguri s'është buton. */}
        <Button variant="contained" disableElevation onClick={() => onSave(form)}
          disabled={saving || leaves.length > 0}
          title={leaves.length ? 'Ka pushim të aprovuar në këtë interval' : undefined}
          startIcon={<CheckCircleRoundedIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2.5, py: .7, fontSize: '0.82rem' }}>
          {saving ? 'Duke ruajtur…' : 'Ruaj orarin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
